import { variables, Flex, Icon, Text, Button, Expand, Buttons } from '@bitrise/bitkit';
import {
  useGetNearestPoint,
  Viz,
  VizHTML,
  VizHTMLArtboard,
  VizSVG,
  VizSVGArea,
  VizSVGAxisX,
  VizSVGAxisY,
  VizSVGCircles,
  VizSVGLine,
  SeriesPointXY,
} from '@bitrise/bitviz';
import { Metric, TypeMetricKey } from '@trace/types';
import max from 'lodash.max';
import React, { PointerEvent, useEffect, useMemo, useState, FunctionComponent } from 'react';
import {
  METRIC_NAME_BY_KEY,
  METRIC_UNIT_BY_KEY,
  METRIC_UNIT_NAME,
} from '../../../constants/Metrics';
import useTrackingEventTimed from '../../Tracking/useTrackingEventTimed';
import getMedian from '../../../utils/getMedian';
import getFormattedMetricValue, { formatterMs } from '../../../utils/metric/getFormattedValue';
import getJoinedNullDataSet from '../../../utils/metric/getJoinedNullDataSet';
import getTicksFromStep from '../../../utils/metric/getTicksFromStep';
import getLastSeriesPoint from '../../../utils/series/getLastSeriesPoint';
import MessageEmptyData from '../../ViewVisualisations/MessageEmptyData';
import transition from '../../ViewVisualisations/transition';
import SessionVisualisationTracker from '../SessionVisualisationTracker';

export interface LineChartProps {
  expanded: boolean;
  end: number;
  focusX: null | number;
  height: number;
  metric?: Metric<'raw'>;
  metricKey: TypeMetricKey;
  onExpand?: () => void;
  onFocusX: (x: null | number) => void;
  start: number;
}

const SessionVisualisation: FunctionComponent<LineChartProps> = (props) => {
  const {
    end,
    expanded,
    focusX,
    height,
    metric,
    metricKey,
    onExpand,
    onFocusX,
    start,
    ...rest
  } = props;
  const [[series, yDomain], setSeries] = useState<[SeriesPointXY[], number[]]>([[], []]);
  const getNearestPoint = useGetNearestPoint(series);
  const xDomain = useMemo(() => [start || 0, end || 1], [start, end]);
  const focusPoint = useMemo(
    () => getNearestPoint({ x: focusX, y: 0 }) || getLastSeriesPoint(series),
    [getNearestPoint, series, focusX],
  );
  const hasDuration = start < end;

  const handlePointerEnter = useTrackingEventTimed('SessionExplorer.Metric.Interaction', {
    metric: metricKey,
  });

  const handleSetFocus = (_: PointerEvent, point?: SeriesPointXY) => {
    if (point) {
      onFocusX(point.x);
    } else {
      onFocusX(null);
    }
  };

  const handlePointerLeave = () => {
    handlePointerEnter.cancel();
    onFocusX(null);
  };

  useEffect(() => {
    if (metric) {
      const xAxisTicks = getTicksFromStep(
        metric.views.raw.start,
        metric.views.raw.end,
        metric.step,
      );
      const newSeries = getJoinedNullDataSet(metric.views.raw.values, {
        withEnd: true,
        withStart: true,
      }).map((y, x) => ({
        x: xAxisTicks[x],
        y,
      }));
      const yLower = 0;
      const yUpper = max(metric.views.raw.values) || 100;

      setSeries([newSeries, [yLower, yUpper]]);
    } else {
      setSeries(([currentSeries, currentYDomain]) => [
        currentSeries.map(({ x }) => ({ x, y: currentYDomain[0] })),
        currentYDomain,
      ]);
    }
  }, [metric]);

  return (
    <Flex
      direction="vertical"
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <Flex direction="horizontal" gap="x2">
        <Flex>
          <Text ellipsis>{METRIC_NAME_BY_KEY[metricKey]}:</Text>
          <Text config="9" ellipsis textColor="gray-5">
            {METRIC_UNIT_NAME[METRIC_UNIT_BY_KEY[metricKey]]}
          </Text>
        </Flex>

        {focusPoint && focusPoint.x && metric && (
          <Flex direction="horizontal" gap="x2">
            <Text inline textColor="aqua-5">
              {getFormattedMetricValue(METRIC_UNIT_BY_KEY[metricKey], focusPoint.y)}
            </Text>{' '}
            <Text inline textColor="gray-6">
              {formatterMs(focusPoint.x - start)}
            </Text>
          </Flex>
        )}

        {onExpand && (
          <Flex grow>
            <Buttons alignChildrenHorizontal="end">
              <Button level="tertiary" onClick={onExpand} size="small">
                <Text>{expanded ? 'Hide' : 'Show'}</Text>
                <Icon name={expanded ? 'MinusClose' : 'PlusOpen'} size="1rem" />
              </Button>
            </Buttons>
          </Flex>
        )}
      </Flex>

      <Expand expanded={expanded} paddingVertical="x4">
        <Viz
          {...rest}
          height={height}
          onPointerMove={handleSetFocus}
          paddingBottom={24}
          paddingLeft={44}
          paddingRight={16}
          paddingTop={12}
          transition={transition}
          xDomain={xDomain}
          yDomain={yDomain}
        >
          {hasDuration && (
            <VizSVG>
              <defs>
                <linearGradient gradientTransform="rotate(90)" id="sessionMetricFillGradient">
                  <stop offset="0%" stopColor={variables.colorAqua3} />
                  <stop offset="100%" stopColor={`rgba(${variables.rgbAqua3}, 0)`} />
                </linearGradient>
              </defs>

              <VizSVGAxisX
                fill={variables.colorGray5}
                format={(x) => formatterMs(x - start)}
                stroke={variables.colorGray5}
                tickValues={[xDomain[0], getMedian(xDomain), xDomain[1]]}
                ticksOnly
              />

              <VizSVGAxisY
                fill={variables.colorGray5}
                flip
                format={(y) => getFormattedMetricValue(METRIC_UNIT_BY_KEY[metricKey], y)}
                stroke={variables.colorGray5}
                tickValues={yDomain}
                ticksOnly
              />

              <VizSVGArea
                animate
                curve="curveMonotoneX"
                fill="url('#sessionMetricFillGradient')"
                fillOpacity="0.15"
                opacity={metric ? 1 : 0}
                originY={yDomain[0]}
                series={series}
              />

              <VizSVGLine
                animate
                curve="curveMonotoneX"
                opacity={metric ? 1 : 0}
                originY={yDomain[0]}
                series={series}
                stroke={variables.colorAqua3}
                strokeWidth="2"
              />

              {focusPoint && (
                <VizSVGCircles fill={variables.colorAqua3} r={6} series={[focusPoint]} />
              )}

              {metric && <SessionVisualisationTracker x={focusX} />}
            </VizSVG>
          )}

          <VizHTML>
            <VizHTMLArtboard>
              <MessageEmptyData empty={!hasDuration} />
            </VizHTMLArtboard>
          </VizHTML>
        </Viz>
      </Expand>
    </Flex>
  );
};

export default SessionVisualisation;
